import { c as _c } from "react/compiler-runtime";
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';

/** Standard query parameter that carries the magic-link access token. */
export const ACCESS_TOKEN_QUERY_PARAM = 'access';
const STORAGE_PREFIX = 'sizzle:access-token:';
function storageKey(objectType: string, objectId: string | number) {
  return `${STORAGE_PREFIX}${objectType}:${String(objectId)}`;
}
function readStoredToken(objectType: string, objectId: string | number): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(storageKey(objectType, objectId));
  } catch {
    return null;
  }
}
function writeStoredToken(objectType: string, objectId: string | number, token: string) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(storageKey(objectType, objectId), token);
  } catch {
    // sessionStorage may be unavailable (private mode, etc.) — silently ignore
  }
}

/**
 * Read the raw access token (if any) carried by the current page.
 *
 * Behavior:
 * - Reads `?access=TOKEN` from the URL and persists it to `sessionStorage`
 *   keyed by `objectType:objectId` so it survives client-side navigations
 *   within the session.
 * - Returns the most recently captured token for that object (URL or storage).
 * - The token is never sent automatically; pass the returned `getHeaders()`
 *   into requests that should be authorized by the grant.
 */
export function useAccessToken(objectType, objectId) {
  const $ = _c(15);
  const router = useRouter();
  let t0;
  bb0: {
    const raw = router.query[ACCESS_TOKEN_QUERY_PARAM];
    if (typeof raw === "string" && raw.length > 0) {
      t0 = raw;
      break bb0;
    }
    if (Array.isArray(raw) && raw[0]) {
      t0 = raw[0];
      break bb0;
    }
    t0 = null;
  }
  const tokenFromUrl = t0;
  let t1;
  let t2;
  if ($[0] !== objectId || $[1] !== objectType || $[2] !== tokenFromUrl) {
    t1 = () => {
      if (!objectId || !tokenFromUrl) {
        return;
      }
      writeStoredToken(objectType, objectId, tokenFromUrl);
    };
    t2 = [objectType, objectId, tokenFromUrl];
    $[0] = objectId;
    $[1] = objectType;
    $[2] = tokenFromUrl;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  bb1: {
    if (tokenFromUrl) {
      t3 = tokenFromUrl;
      break bb1;
    }
    if (objectId === undefined || objectId === null) {
      t3 = null;
      break bb1;
    }
    let t4;
    if ($[5] !== objectId || $[6] !== objectType) {
      t4 = readStoredToken(objectType, objectId);
      $[5] = objectId;
      $[6] = objectType;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    t3 = t4;
  }
  const token = t3;
  let t4;
  let t5;
  if ($[8] !== token) {
    t4 = () => token ? {
      [ACCESS_TOKEN_QUERY_PARAM]: token
    } : undefined;
    t5 = () => token ? {
      "x-access-token": token
    } : undefined;
    $[8] = token;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5 || $[13] !== token) {
    t6 = {
      token,
      getSearchParams: t4,
      getHeaders: t5
    };
    $[11] = t4;
    $[12] = t5;
    $[13] = token;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}